/**
 * humanizeAction (Story 34.5 — Epic 34). Renders an audit action + its context
 * into a natural-language sentence (markdown `**bold**` for the subject). Pure +
 * reusable — the /activity timeline, future email digests, and Slack notifs all
 * share this one renderer. Every action in the ACTIONS vocabulary maps to a
 * sentence (no known action falls through to the generic fallback — enforced by
 * a table-driven test).
 */

export interface HumanizeOpts {
  /** Frozen actor email (for actions whose subject is a person). */
  actorEmail?: string | null;
  /** The resource's human name at event time (resource_name_snapshot). */
  resourceName?: string | null;
  /** The captured field diff (for *.updated / role_changed). */
  diff?: { before?: Record<string, unknown>; after?: Record<string, unknown> } | null;
  /** Action-specific payload (e.g. invitee email, viewed ids). */
  metadata?: Record<string, unknown> | null;
}

const RESOURCE_NOUN: Record<string, string> = {
  report: "report",
  report_template: "report template",
  client: "client",
  custom_metric: "custom metric",
  client_goal: "goal",
  client_budget: "budget",
  client_markup: "markup",
  tag: "tag",
  integration: "integration",
  portal_link: "share link",
  dashboard_share_link: "dashboard share link",
  brand_share_link: "brand share link",
  dashboard: "dashboard",
  brand: "brand",
  schedule: "schedule",
  task: "task",
  alert: "alert",
  team_member: "team member",
  workspace: "workspace",
  account: "account",
  tenant_settings: "workspace settings",
  tenant_branding: "branding",
  user_settings: "profile",
  audit: "audit log",
  billing: "billing",
};

function titleCase(value: unknown): string {
  const s = String(value ?? "");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "unknown";
}

function namePart(name: string | null | undefined): string {
  return name ? ` **${name}**` : "";
}

/**
 * First argument that is a non-empty string, else undefined. Unlike `??`, this
 * treats `""` as absent — so a blank resource name / email snapshot falls back
 * to the generic subject ("a team member") instead of rendering `****`.
 */
function firstNonEmpty(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

const VERB_PHRASE: Record<string, (noun: string, name: string) => string> = {
  created: (noun, n) => `created the ${noun}${n}`,
  updated: (noun, n) => `updated the ${noun}${n}`,
  deleted: (noun, n) => `deleted the ${noun}${n}`,
  archived: (noun, n) => `archived the ${noun}${n}`,
  restored: (noun, n) => `restored the ${noun}${n}`,
  connected: (noun, n) => `connected the ${noun}${n}`,
  disconnected: (noun, n) => `disconnected the ${noun}${n}`,
  discard_requested: (noun, n) => `requested removal of the ${noun}${n}`,
  revoked: (noun, n) => `revoked the ${noun}${n}`,
  submitted: (noun, n) => `submitted the ${noun}${n} for review`,
  cloned: (noun, n) => `cloned the ${noun}${n}`,
};

export function humanizeAction(action: string, opts: HumanizeOpts = {}): string {
  const dot = action.indexOf(".");
  const resource = dot >= 0 ? action.slice(0, dot) : action;
  const verb = dot >= 0 ? action.slice(dot + 1) : "";
  const noun = RESOURCE_NOUN[resource] ?? resource.replace(/_/g, " ");
  const name = firstNonEmpty(opts.resourceName) ?? null;

  // Resource/verb-specific phrasing.
  switch (action) {
    case "team_member.role_changed": {
      const before = titleCase(opts.diff?.before?.role);
      const after = titleCase(opts.diff?.after?.role);
      const who = firstNonEmpty(name, opts.metadata?.email) ?? "a team member";
      return `changed **${who}**'s role from ${before} to ${after}`;
    }
    case "team_member.invited":
      return `invited **${firstNonEmpty(name, opts.metadata?.inviteeEmail) ?? "a teammate"}** to the team`;
    case "team_member.removed":
      return `removed **${name ?? "a team member"}** from the team`;
    case "portal_link.consumed":
    case "dashboard_share_link.consumed":
    case "brand_share_link.consumed":
      return `viewed${namePart(name) || " content"} via a share link`;
    case "account.deletion_requested":
      return "requested account deletion";
    case "account.deletion_cancelled":
      return "cancelled account deletion";
    case "audit.cold_accessed":
      return "accessed the cold audit archive";
    case "workspace.deleted":
      return "deleted the workspace";
    case "billing.checkout_started":
      return "started a subscription checkout";
    case "billing.checkout_canceled":
      return "canceled a subscription checkout";
    case "billing.portal_opened":
      return "opened the billing portal";
    case "billing.interval_switched":
      return "changed the subscription billing interval";
    case "billing.overage_confirmed":
      return "confirmed an overage charge";
    case "billing.subscription_activated":
      return "activated the subscription";
    case "billing.subscription_changed":
      return "changed the subscription";
    case "billing.subscription_ended":
      return "ended the subscription";
    case "billing.payment_failed":
      return "recorded a failed subscription payment";
    case "billing.payment_recovered":
      return "recorded a recovered subscription payment";
  }

  const fn = VERB_PHRASE[verb];
  if (fn) return fn(noun, namePart(name));
  return `performed ${action}`;
}
